import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

const AboutPage = () => {
  // SEO Configuration
  useSEO({
    title: 'About CerviShield | AI-Powered Cervical Cancer Awareness Platform',
    description: 'Learn about CerviShield\'s mission to improve cervical cancer awareness and early detection through AI technology. Created by data scientist Sarthak Bobade to make healthcare accessible to all women in India.',
    keywords: 'CerviShield about, cervical cancer awareness mission, AI healthcare, data science health, women\'s health technology',
    canonical: '/about',
    schema: generateWebPageSchema(
      'About CerviShield - Our Mission and Vision',
      'Using data science and AI to improve cervical cancer awareness and healthcare access',
      '/about'
    ),
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative h-96 flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary to-teal-700"></div>
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/63e164aca36d5a4dfa31139170beea11.jpg"
            alt="About"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-white">
          <h1 className="text-5xl md:text-6xl font-bold font-display mb-4">
            About CerviShield
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Using data science and AI to improve cervical cancer awareness and healthcare access
          </p>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-white to-teal-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h2 className="text-4xl font-bold font-display text-gray-800 mb-6">
                Our <span className="text-primary">Vision</span>
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  CerviShield was created with a simple yet powerful vision: <strong>to make cervical cancer awareness and early detection accessible to everyone</strong>, regardless of their location or economic status.
                </p>
                <p>
                  Cervical cancer is one of the most preventable cancers, yet thousands of women lose their lives each year due to lack of awareness and delayed detection. We believe that technology and data science can bridge this gap.
                </p>
                <p>
                  By combining machine learning with educational resources, we aim to empower women with knowledge, guide them toward preventive care, and ultimately contribute to reducing cervical cancer mortality rates.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20healthcare%20scene%20showing%20diverse%20group%20of%20female%20patients%20and%20medical%20professionals%20in%20modern%20hospital%20consultation%20room%2C%20realistic%20medical%20photography%20with%20natural%20lighting%2C%20women%20of%20different%20ages%20and%20ethnicities%20sitting%20together%20with%20female%20doctors%20in%20white%20coats%20discussing%20health%20information%2C%20warm%20welcoming%20atmosphere%20emphasizing%20community%20healthcare%20and%20patient%20empowerment%2C%20teal%20and%20white%20color%20scheme%2C%20clean%20modern%20medical%20facility%20interior%20with%20medical%20charts%20and%20health%20education%20materials%20visible%2C%20focus%20on%20trust%20collaboration%20and%20preventive%20care%2C%20photorealistic%20style%20with%20soft%20professional%20lighting&width=700&height=600&seq=vision-realistic-medical-002&orientation=landscape"
                alt="Vision"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-12 mb-24">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-12 text-center">
              Our <span className="text-primary">Mission</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-lightbulb-line',
                  title: 'Raise Awareness',
                  description: 'Educate women about cervical cancer, risk factors, and the importance of regular screening through accessible information.',
                  color: 'yellow',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Enable Early Detection',
                  description: 'Provide AI-assisted risk assessment tools that help identify individuals who may benefit from medical screening.',
                  color: 'teal',
                },
                {
                  icon: 'ri-hospital-line',
                  title: 'Improve Access',
                  description: 'Connect users with healthcare facilities and resources, making preventive care more accessible and affordable.',
                  color: 'blue',
                },
              ].map((mission, index) => (
                <div key={index} className="text-center">
                  <div className={`w-20 h-20 flex items-center justify-center bg-${mission.color}-100 rounded-full mx-auto mb-6`}>
                    <i className={`${mission.icon} text-4xl text-${mission.color}-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{mission.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{mission.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-24">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-12 text-center">
              How We <span className="text-primary">Make Impact</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  icon: 'ri-brain-line',
                  title: 'Data-Driven Approach',
                  description: 'We use machine learning models trained on medical research data to provide personalized risk assessments. Our AI analyzes multiple risk factors to offer guidance tailored to each individual.',
                },
                {
                  icon: 'ri-book-open-line',
                  title: 'Educational Resources',
                  description: 'We provide comprehensive, easy-to-understand information about cervical cancer, prevention strategies, and screening guidelines based on WHO and medical research.',
                },
                {
                  icon: 'ri-shield-user-line',
                  title: 'Privacy-First Design',
                  description: 'We process all data anonymously without storage. Your privacy and security are our top priorities, ensuring you can use our platform with complete confidence.',
                },
                {
                  icon: 'ri-community-line',
                  title: 'Community Focus',
                  description: 'We prioritize accessibility by highlighting government hospitals and affordable care options, ensuring that quality healthcare guidance is available to all socioeconomic groups.',
                },
              ].map((impact, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-primary"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 flex items-center justify-center bg-primary/10 rounded-xl flex-shrink-0">
                      <i className={`${impact.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{impact.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{impact.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-accent/10 to-purple-50 rounded-3xl p-12 mb-24">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img
                  src="https://static.readdy.ai/image/6006b18c50b98974f62513d00886a423/7135bcec3c75d7254f1439a31b133be3.jpeg"
                  alt="Sarthak Bobade - Data Scientist"
                  className="w-full h-auto rounded-2xl shadow-lg"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold font-display text-gray-800 mb-6">
                  About the <span className="text-accent">Creator</span>
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    CerviShield was developed by <strong>Sarthak Bobade</strong>, a passionate <strong>Data Scientist</strong> focused on building AI-powered solutions that improve access to healthcare and support early disease awareness.
                  </p>
                  <p>
                    With expertise in <strong>healthcare AI, machine learning, and public health analytics</strong>, Sarthak works on applying data analysis and technology to help individuals make better health decisions especially in women's and preventive healthcare.
                  </p>
                  <p>
                    Through platforms like <strong>CerviShield and MomsFirstCare</strong>, his goal is to use data responsibly to make healthcare more accessible, affordable, and informed for everyone.
                  </p>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  {['Data Science', 'Healthcare AI', 'Public Health Analytics', 'Women\'s Healthcare'].map((skill, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-accent/20 text-accent rounded-full text-sm font-semibold"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
                <div className="mt-6">
                  <a
                    href="https://www.linkedin.com/in/sarthak-bobade-3498702b5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A66C2] text-white rounded-lg font-semibold hover:bg-[#004182] transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-linkedin-fill text-xl"></i>
                    Connect on LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-12 mb-24">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-8 text-center">
              AI <span className="text-primary">Transparency</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-6">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  What Our AI Does
                </h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-0.5 flex-shrink-0"></i>
                    <span>Analyzes risk factors based on medical research</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-0.5 flex-shrink-0"></i>
                    <span>Provides educational risk assessment categories</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-0.5 flex-shrink-0"></i>
                    <span>Offers personalized preventive care guidance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-0.5 flex-shrink-0"></i>
                    <span>Directs users to appropriate healthcare resources</span>
                  </li>
                </ul>
              </div>
              <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <i className="ri-close-line text-red-600 text-xl"></i>
                  What Our AI Does NOT Do
                </h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-red-600 mt-0.5 flex-shrink-0"></i>
                    <span>Does NOT diagnose cervical cancer or any disease</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-red-600 mt-0.5 flex-shrink-0"></i>
                    <span>Does NOT replace medical professionals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-red-600 mt-0.5 flex-shrink-0"></i>
                    <span>Does NOT prescribe treatments or medications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-red-600 mt-0.5 flex-shrink-0"></i>
                    <span>Does NOT guarantee accuracy of predictions</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 bg-blue-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed">
                <strong>Our commitment:</strong> We use AI ethically and responsibly. Our models are based on established medical research, but they are educational tools only. Always consult qualified healthcare professionals for medical decisions.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-primary to-teal-600 rounded-3xl p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Join Us in Making a Difference</h2>
            <p className="text-xl mb-8 leading-relaxed max-w-3xl mx-auto">
              Together, we can raise awareness, promote early detection, and save lives. Start your cervical health journey today.
            </p>
            <Link
              to="/consent"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-primary rounded-full text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Start Health Check
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
